import React, {useMemo} from "react";
import JsBarcode from "jsbarcode";
import {computeEAN13CheckDigit} from "../../utils/barcodeGenerator";

/**
 * Détermine le symbole le plus adapté à la valeur fournie.
 * EAN13 si 13 chiffres avec chiffre de contrôle valide, sinon CODE128 (alphanumérique).
 */
function pickFormat(value) {
    if (/^\d{13}$/.test(value) && computeEAN13CheckDigit(value.slice(0, 12)) === value[12]) {
        return "EAN13";
    }
    return "CODE128";
}

/**
 * Cache du markup SVG par (valeur + dimensions). Permet de générer le code-barres
 * une seule fois même lorsqu'une même étiquette est imprimée en grande quantité :
 * JsBarcode (impératif, coûteux) n'est appelé qu'une fois par combinaison distincte,
 * les copies suivantes ne font qu'une copie de chaîne.
 */
const markupCache = new Map();

function getBarcodeMarkup(text, {height, width, displayValue}) {
    const fmt = pickFormat(text);
    const key = `${text}|${height}|${width}|${displayValue}|${fmt}`;
    if (markupCache.has(key)) return markupCache.get(key);

    let markup = "";
    try {
        // SVG détaché : JsBarcode dessine dedans, on sérialise le résultat.
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        JsBarcode(svg, text, {format: fmt, height, width, displayValue, margin: 0, fontSize: 12});
        markup = svg.outerHTML;
    } catch {
        // Valeur incompatible avec le symbole : on n'affiche rien plutôt que de planter.
        markup = "";
    }
    markupCache.set(key, markup);
    return markup;
}

// Garantit que le SVG injecté ne déborde pas de l'étiquette.
if (typeof document !== "undefined" && !document.getElementById("qc-barcode-style")) {
    const s = document.createElement("style");
    s.id = "qc-barcode-style";
    s.textContent = ".qc-barcode svg{max-width:100%;height:auto;display:block}";
    document.head.appendChild(s);
}

/**
 * Rend un code-barres en SVG via JsBarcode (avec mise en cache).
 *
 * @param {string} value   - Donnée à encoder.
 * @param {number} height  - Hauteur des barres (px).
 * @param {number} width   - Largeur d'un module (px).
 * @param {boolean} displayValue - Afficher la valeur sous les barres.
 */
const BarcodeRenderer = ({value, height = 40, width = 1.4, displayValue = true, className = ""}) => {
    const text = value == null ? "" : String(value).trim();
    const markup = useMemo(
        () => (text ? getBarcodeMarkup(text, {height, width, displayValue}) : ""),
        [text, height, width, displayValue]
    );

    if (!text || !markup) return null;

    return (
        <span
            className={`qc-barcode ${className}`}
            style={{maxWidth: "100%", display: "inline-flex"}}
            dangerouslySetInnerHTML={{__html: markup}}
        />
    );
};

export default BarcodeRenderer;
